import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

const DELETE_VERSION_ACCESS_TYPES = [Action.SpaceAdmin, Action.UpdateBlueprintsVersionedGroup];

/**
 * Hook that returns a function to check if the current user can delete a version.
 * A user needs both the UpdateBlueprintsVersionedGroup permission action AND write access (or admin) to the template's space to delete a version.
 * A user can't delete a version that has deployments.
 * @returns {(spaceId: string, deploymentsCount: number) => AccessResult} Function that takes a version, space ID, and deployments count and returns an AccessResult indicating if user can delete it
 */
const useCanDeleteVersion = (): ((spaceId: string, deploymentsCount: number) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (spaceId: string, deploymentsCount: number) => {
      const hasAccess = hasAtLeastOneRequiredAction(DELETE_VERSION_ACCESS_TYPES, viewer, spaceId);

      if (!hasAccess) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (deploymentsCount > 0) {
        return {
          result: false,
          message: "Cannot delete version with existing deployments",
        };
      }

      return { result: true };
    },
    [viewer]
  );
};

export default useCanDeleteVersion;
